'use client';

import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  isRateLimitedPublicSearchType,
  SEARCH_TYPE,
} from '@/constants/searchType';
import usePublicSearchLimit from '@/components/publicSearch/heroSection/usePublicSearchLimit';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { useSaleIdParam } from '@/hooks/publicSearch/useSaleIdParam';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { useAnimalEbvFilter } from './animal/useAnimalEbvFilter';
import { useSearchType } from './useSearchType';
import { useSaleCatalogueFilter } from './sale-catalogue/useSaleCatalogueFilter';
import { useSaleCatalogueDetailAnimalsFilter } from './sale-catalogue/useSaleCatalogueDetailAnimalsFilter';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useMembersFilter } from './useMembersFilter';
import { isFilterInputIncompleteForQuery } from '@/components/publicSearch/search/searchFilterEbvExpansion';

// Internal: actually mounts the four list-filter hooks (animal / members /
// sales / sale-detail) and exposes the active one's result. Calling this from
// multiple components in the same tree would mount the hooks (and their lazy
// queries) once per call, which means N parallel `getX` executes per page
// render and N network POSTs for the active searchType. Always go through
// the SearchProvider + useSearch() pair below instead — that guarantees a
// single instance per SocietySearchPage subtree.
function useSearchImpl() {
  const params = useSearchParams();
  const saleId = useSaleIdParam();
  const lotNo = params.get(SEARCH_PARAM.LOT_NO);
  const searchType = useSearchType();
  const { filters, filterGroups } = useFilterStore(searchType);
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const { hasReachedSearchLimit } = usePublicSearchLimit();
  const guestSearchLimitBlocksQuery =
    hasReachedSearchLimit && isRateLimitedPublicSearchType(searchType);
  const filtersArray = Array.isArray(filters) ? filters : [];
  const hasGroupedFilters = filterGroups.length > 0;
  const skipQuery =
    !orgId ||
    (!hasGroupedFilters &&
      filtersArray.length > 0 &&
      filtersArray.some(isFilterInputIncompleteForQuery));

  const animalQuery = useAnimalEbvFilter(
    searchType === SEARCH_TYPE.ANIMAL &&
      !skipQuery &&
      !guestSearchLimitBlocksQuery,
  );
  const membersQuery = useMembersFilter(
    searchType === SEARCH_TYPE.MEMBERS &&
      !skipQuery &&
      !guestSearchLimitBlocksQuery,
  );
  const salesQuery = useSaleCatalogueFilter(
    searchType === SEARCH_TYPE.SALE_CATALOGUES &&
      !skipQuery &&
      !guestSearchLimitBlocksQuery,
  );
  const saleCatalogueDetailAnimalsQuery = useSaleCatalogueDetailAnimalsFilter(
    { saleId, lotNo },
    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType) &&
      !skipQuery &&
      !guestSearchLimitBlocksQuery,
  );

  // Pick the active query result during render via useMemo rather than
  // mirroring it into useState + useEffect. A setState-on-effect approach lags
  // a render behind the lazy queries, so the grid flashes its stale empty/
  // loading state before repainting with the new rows; deriving in render shows
  // the new data on the same render the lazy query state changed.
  return useMemo(() => {
    const query = (() => {
      if (searchType === SEARCH_TYPE.ANIMAL) return animalQuery;
      if (searchType === SEARCH_TYPE.MEMBERS) return membersQuery;
      if (DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType))
        return saleCatalogueDetailAnimalsQuery;

      if (searchType === SEARCH_TYPE.SALE_CATALOGUES) return salesQuery;
      return animalQuery;
    })();

    return {
      ...query,
      skipQuery,
      guestSearchLimitBlocksQuery,
      searchActive: !skipQuery && !guestSearchLimitBlocksQuery,
    };
  }, [
    animalQuery,
    membersQuery,
    salesQuery,
    saleCatalogueDetailAnimalsQuery,
    searchType,
    skipQuery,
    guestSearchLimitBlocksQuery,
  ]);
}

type SearchValue = ReturnType<typeof useSearchImpl>;

const SearchContext = createContext<SearchValue | null>(null);

/**
 * Provides a single shared search-operations result to every consumer of
 * `useSearch()` within the SocietySearchPage subtree. Mount this once near
 * the page root — calling `useSearch()` outside this provider throws.
 */
export function SearchProvider({ children }: PropsWithChildren) {
  const value = useSearchImpl();
  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useSearch(): SearchValue {
  const value = useContext(SearchContext);
  if (value == null) {
    throw new Error(
      'useSearch() must be used inside <SearchProvider>. Wrap the public ' +
        'search page (or relevant subtree) with <SearchProvider> so all ' +
        'consumers share one set of underlying lazy queries.',
    );
  }
  return value;
}
